import { useEffect, useState } from "react";
import { Button, Form, Image, Stack, Fade, Alert } from "react-bootstrap";
import Loading from "../components/Loading";
import { useAuth } from "../api/auth/store";
import Label from "../components/Label";
import { useNavigate, useSearchParams } from "react-router";
import { getErrorMessage } from "../utils/errors";
import { ERR_FORGOT_EXPIRED, ERR_FORGOT_INVALID, ERR_FORGOT_USED, HTTP_OK } from "../constants";
import Logo from "../assets/images/logo.png";
import { validatePasswordComplexity } from "../utils/validation";

const ResetPasswordPage = () => {
  const { landingIcon, validateForgot, resetPassword } = useAuth(state => state);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errmsg, setErrmsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const code = searchParams.get("code");

  const onReset = async () => {
    setErrmsg("");
    setLoading(true);
    setSuccess(false);

    if (password !== confirmPassword) {
      setErrmsg("Passwords do not match.");
      setLoading(false);
      return;
    }

    const validationRes = validatePasswordComplexity(password);
    if (validationRes) {
      setErrmsg(validationRes);
      setLoading(false);
      return;
    }

    const res = await resetPassword(code!, username, password);
    setLoading(false);
    switch (res) {
      case HTTP_OK:
        setSuccess(true);
        break;
      default:
        console.error("Password reset failed. Error code:", res);
        setErrmsg(getErrorMessage(res) || `Failed to reset password (${res}). Please try again later.`);
        break;
    }
  };

  const updateInputs = (_password: string, _confirmPassword: string) => {
    if (_password !== "") setPassword(_password);
    if (_confirmPassword !== "") setConfirmPassword(_confirmPassword);
    setErrmsg("");
  };

  const logo = landingIcon ? landingIcon : Logo;

  // Validate the code on component mount
  useEffect(() => {
    if (code) {
      validateForgot(code).then((res) => {
        if (res.Code === HTTP_OK) {
          setValidated(true);
          if (res.Msg) {
            setUsername(res.Msg);
          }
        }
        else {
          navigate("/forgot");
        }
      });
    } else {
      navigate("/forgot");
    }
  }, []);

  if (!validated) {
    return <Loading center />;
  }

  return (
    <Stack className="login-box" gap={3}>
      <Image className="login-img" src={logo} />
      {success ? (
        <Stack gap={2} className="text-center">
          <Alert variant="success">
            <Label color="green" size="medium" fontWeight="600">
              Password reset successful!
            </Label>
          </Alert>
          <p className="text-muted">
            You can now login with your new password.
          </p>
          <Button onClick={() => navigate("/")}>Go to Login</Button>
        </Stack>
      ) : (
        <>
          <Stack gap={2}>
            <Label size="large" fontWeight="600" textAlign="center">
              Reset Password
            </Label>
            <p className="text-muted text-center">
              Enter your new password below.
            </p>
          </Stack>
          <Form.Control
            autoFocus
            disabled={loading || username !== ""}
            placeholder="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <Form.Control
            disabled={loading}
            placeholder="New Password"
            type="password"
            onChange={(e) => updateInputs(e.target.value, "")}
          />
          <Form.Control
            disabled={loading}
            placeholder="Confirm New Password"
            type="password"
            onChange={(e) => updateInputs("", e.target.value)}
          />
          {errmsg !== "" && (
            <Alert variant="danger" className="mt-2">
              {errmsg}
            </Alert>
          )}
          <Fade in={password !== "" && confirmPassword !== ""}>
            <Button className="login-button" disabled={loading || password === "" || confirmPassword === ""} onClick={onReset}>
              {loading ? <Loading /> : "Reset Password"}
            </Button>
          </Fade>
        </>
      )}
    </Stack>
  );
};

export default ResetPasswordPage;
